import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { MatDialog } from '@angular/material/dialog';
import { EditEventDatesDialogComponent } from './edit-dialog/edit-event-dates-dialog.component';
import dynamic from 'ajv/dist/vocabularies/dynamic';
import { TranslateService } from '@services/translate/translate.service';
import { MessengerService } from '@services/messenger/messenger.service';
import { SmartInputComponent } from '../../../../../components/smart-input/smart-input.component';

@Component({
    selector: 'event-dates',
    templateUrl: './event-dates.component.html',
    styleUrls: ['./event-dates.component.scss'],
    standalone: false
})

export class EventDatesComponent implements OnInit, OnChanges {
  @ViewChild('startDate') startDate: SmartInputComponent;
  @Input() dynamicContent: Models.DynamicContent;
  weekdays = { 0: true, 1: true, 2: true, 3: true, 4: true, 5: true, 6: true };
  weekdayArray = [1, 2, 3, 4, 5, 6, 0];
  translations;
  description;
  superadmin = false;

  constructor(private dialog: MatDialog, private translate: TranslateService, private ms: MessengerService) {
    this.ms.$superadminPrivileges.subscribe(privileges => {
      this.superadmin = !!(privileges || []).find(el => el.privilegeName === 'superadmin' && el.value === 'packages');
    });
  }

  async ngOnInit() {
    this.translations = this.translate.get();
    if (!this.dynamicContent?.content?.multipleDates) {
      this.dynamicContent.content.multipleDates = [];
      if (!!this.dynamicContent?.content?.dates) {
        this.dynamicContent.content.multipleDates.push(this.dynamicContent.content.dates);
      }
    }
    this.dynamicContent.content.multipleDates = this.dynamicContent.content.multipleDates.sort((a, b) => {
      if (a.startDate === b.startDate) {
        return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
      } else {
        return new Date(a.startDate).getTime() - new Date(b.endStartDate).getTime();
      }
    });
    if (this.dynamicContent?.content?.repetition?.weekdays) {
      Object.keys(this.weekdays).forEach(key => {
        if (!this.dynamicContent.content.repetition.weekdays.find(day => day === key)) {
          this.weekdays[key] = false;
        }
      })
    }
    this.updateDescription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.dynamicContent?.currentValue?.content?.repetition) {
      this.dynamicContent.content.repetition = <Models.DynamicContentBase>Models.ModelFactory.createModelInstanceByName('EventRepetition', this.dynamicContent.content.repetition)
    }
  }

  edit(index: number) {
    this.editEventDate(index);
  }

  remove(index: number) {
    this.dynamicContent.content.multipleDates.splice(index, 1);
  }

  add() {
    this.editEventDate(undefined);
  }

  async editEventDate(index: number) {
    const data = { element: this.dynamicContent.content.multipleDates[index] ? JSON.parse(JSON.stringify(this.dynamicContent.content.multipleDates[index])) : null };
    const eventDate: Models.OpeningInfo[] = await this.dialog.open(EditEventDatesDialogComponent, { width: '580px', autoFocus: false, data }).afterClosed().toPromise();
    if (!!eventDate) {
      if (index === undefined) {
        this.dynamicContent.content.multipleDates.push(eventDate);
      } else {
        this.dynamicContent.content.multipleDates[index] = eventDate;
      }
    }
  }

  toggleRepeating(event: any) {
    this.dynamicContent.content.repeating = !this.dynamicContent.content.repeating;
    if (!!this.dynamicContent.content.repeating && !this.dynamicContent.content.repetition) {
      this.dynamicContent.content.repetition = new Models.EventRepetition();
      this.dynamicContent.content.repetition.startDate = new Date();
      this.dynamicContent.content.repetition.weekdays = [this.dynamicContent.content.repetition.startDate.getDay()];
      Object.keys(this.weekdays).forEach(k => { this.weekdays[k] = parseInt(k) === this.dynamicContent.content.repetition.startDate.getDay() });
    } else if (!this.dynamicContent.content.repeating) {
      this.dynamicContent.content.repetition = null;
    }
  }

  toggleRepetitionExact(event: any) {
    if (this.dynamicContent.content.repetition.repetitionLastDay) {
      this.dynamicContent.content.repetition.repetitionLastDay = false;
      this.dynamicContent.content.repetition.repetitionExact = event?.senderId === 'repetitionExact';
    } else {
      this.dynamicContent.content.repetition.repetitionExact = !this.dynamicContent.content.repetition.repetitionExact;
    }
    this.updateDescription();
  }

  toggleRepetitionLastDay() {
    this.dynamicContent.content.repetition.repetitionLastDay = !this.dynamicContent.content.repetition.repetitionLastDay;
    this.dynamicContent.content.repetition.repetitionExact = true;
  }

  isLastOfMonth() {
    const testDate = new Date(this.dynamicContent.content.repetition.startDate);
    testDate.setDate(testDate.getDate() + 1);
    return testDate.getDate() === 1;
  }

  getExactTitle(type = 'exact') {
    let res;
    if (type === 'exact') {
      res = 'Findet am ' + this.dynamicContent?.content?.repetition?.startDate?.getDate?.() + '. Tag des Monats';
    } else {
      const nth = this.translations.general.ordinalNumbers[this.getWeekdayOccurrence(this.dynamicContent?.content?.repetition?.startDate)];
      const weekday = this.translations.general.days[this.dynamicContent?.content?.repetition?.startDate?.getDay?.()];
      res = 'Findet am ' + nth + ' ' + weekday + ' des Monats';
    }
    if (this.dynamicContent.content.repetition.repetitionSize === 'year') {
      res += ' ' + this.translations.general.months[this.dynamicContent?.content?.repetition?.startDate?.getMonth?.() + 1];
    }
    return res + ' statt.';
  }

  getWeekdayOccurrence(date) {
    const currentDate = new Date(date);
    currentDate.setDate(1);
    const firstOccurrence = 1 + ((date.getDay() - currentDate.getDay() + 7) % 7);
    return Math.floor((date.getDate() - firstOccurrence) / 7) + 1;
  }

  updateDays(i) {
    this.weekdays[i] = !this.weekdays[i];
    this.dynamicContent.content.repetition.weekdays = Object.keys(this.weekdays).filter(key => this.weekdays[key]);
    const weekdays = JSON.parse(JSON.stringify(this.dynamicContent.content.repetition.weekdays)).map(el => parseInt(el)).map(el => el === 0 ? 7 : el).sort();
    if (weekdays?.length) {
      this.dynamicContent.content.repetition.startDate = this.setDateToWeekday(this.dynamicContent.content.repetition.startDate, weekdays[0]);
      this.startDate.reloadValue();
    }
    this.updateDescription();
  }

  setDateToWeekday(date, targetWeekday) {
    date.setDate(date.getDate() + (targetWeekday - (date.getDay() || 7)));
    return date;
  }

  updateRepetitionSize() {
    if (['day', 'week'].includes(this.dynamicContent.content.repetition.repetitionSize)) {
      this.weekdays = { 0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false };
      this.updateDays(this.dynamicContent.content.repetition.startDate.getDay());
      this.weekdayArray = [1, 2, 3, 4, 5, 6, 0];
    } else {
      this.dynamicContent.content.repetition.repetitionLastDay = false;
      this.dynamicContent.content.repetition.repetitionExact = true;
    }
  }

  updateDescription() {
    const { repetitionSize: size, repetitionRhythm: rhythm } = this.dynamicContent?.content?.repetition || {};
    if ((size === 'day' || size === 'week') && !this.dynamicContent?.content?.repetition?.weekdays?.length) {
      this.description = 'Bitte wählen Sie mindestens einen Wochentag aus!';
    } else if (size === 'day' || (size === 'week' && rhythm === '1')) {
      this.handleDay();
    } else if (size === 'week' && rhythm !== '1') {
      this.description = 'Findet jeden ' + this.translations.general.ordinalNumbers[this.dynamicContent.content.repetition.repetitionRhythm] + ' ' + this.getDayString() + ' statt.';
    } else if (size === 'month') {
      this.description = 'Test';
    } else if (size === 'year') {
      this.description = 'Test';
    }
  }

  handleDay() {
    if (this.dynamicContent?.content?.repetition?.repetitionRhythm === '1') {
      if (this.dynamicContent?.content?.repetition?.weekdays?.length === 7) {
        return this.description = 'Findet jeden Tag statt.';
      } else {
        return this.description = 'Findet jeden ' + this.getDayString() + ' statt.';
      }
    } else {
      return this.description = 'Findet jeden ' + this.translations?.general?.ordinalNumbers?.[this.dynamicContent?.content?.repetition?.repetitionRhythm] + ' Tag statt.';
    }
  }

  getDayString() {
    let dayString = '';
    if (this.dynamicContent?.content?.repetition?.weekdays?.length === 1) {
      dayString += this.translations.general.days[this.dynamicContent.content.repetition.weekdays[0]];
    } else {
      for (let i  = 0; i < this.dynamicContent?.content?.repetition?.weekdays?.length; i++) {
        if (i === this.dynamicContent?.content?.repetition?.weekdays?.length - 1) {
          dayString += 'und ' + this.translations.general.days[this.dynamicContent.content.repetition.weekdays[i]];
        } else if (i === this.dynamicContent?.content?.repetition?.weekdays?.length - 2) {
          dayString += this.translations.general.days[this.dynamicContent.content.repetition.weekdays[i]] + ' ';
        } else {
          dayString += this.translations.general.days[this.dynamicContent.content.repetition.weekdays[i]] + ', ';
        }
      }
    }
    return dayString;
  }

  protected readonly dynamic = dynamic;
}
